// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, concat } from "rxjs"
import { Action } from "redux"
import { filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TimesheetFieldsActions from "../../timesheet-fields/actions"
import * as Slice from "../slices/timeline"
import type { RootState } from "../../../app/store"
import * as UserContextSlice from "../../user-context/slice"
import { isDisabledTimeGroup, groupUnpostedTimesheetFieldOptionsBySiteId, groupTimesheetFieldOptionsBySiteId } from "../timeGroups"
import { generateTimesheetFieldCachesRefreshActions } from "./lib/generateTimesheetFieldCachesRefreshActions"

export const refreshTimesheetFieldConfigs = () =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter((action) => 
        UserContextSlice.setCurrentSiteId.match(action) ||
        Actions.refreshTimesheetFieldConfigs.match(action)
      ),
      withLatestFrom(state$),
      mergeMap(([_, state]) => {
        const unpostedTimeGroups = Object.values(Slice.selectTimeGroupsCache(state))
          .filter(timeGroup => !isDisabledTimeGroup(timeGroup.postStatus))
        const timeRows = Object.values(Slice.selectTimeRowsCache(state))
        if (!unpostedTimeGroups.length && !timeRows.length) {
          return EMPTY
        }
        
        const actions: Action[] = []
        
        // Generate timesheet field configs for unposted time groups
        if (unpostedTimeGroups.length) {
          actions.push(Actions.generateTimesheetFieldConfigs(unpostedTimeGroups))
        }
        
        // Generate timesheet field configs for current site when time rows exist
        if (timeRows.length) {
          const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
          if (currentSiteId) {
            actions.push(
              TimesheetFieldsActions.generateTimesheetFieldConfigs({ siteId: currentSiteId })
            )
          }
        }
        
        // Batch get timesheet field options from unposted time groups and time rows
        const unpostedOptionsBySiteId = groupUnpostedTimesheetFieldOptionsBySiteId(unpostedTimeGroups)
        const timeRowTimesheetOptionsBySiteId = groupTimesheetFieldOptionsBySiteId(timeRows)
        
        // Merge timesheet field options from both time groups and time rows by site ID
        const mergedOptionsBySiteId = { ...unpostedOptionsBySiteId }
        Object.entries(timeRowTimesheetOptionsBySiteId).forEach(([siteId, options]) => {
          if (mergedOptionsBySiteId[siteId]) {
            mergedOptionsBySiteId[siteId] = [...(mergedOptionsBySiteId[siteId] || []), ...options]
          } else {
            mergedOptionsBySiteId[siteId] = options
          }
        })
        
        const cachedTimesheetFields = Object.values(state.timeline.timesheetFieldsCache)
        const cachedTimesheetFieldOptions = state.timeline.timesheetFieldOptionsCache
        const requiredTimesheetFieldActions = generateTimesheetFieldCachesRefreshActions(
          mergedOptionsBySiteId,
          cachedTimesheetFields,
          cachedTimesheetFieldOptions,
        )
        actions.push(...requiredTimesheetFieldActions)
        
        return concat(actions)
      }),
    )

export default refreshTimesheetFieldConfigs()
